import { useState } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Home,
  Building2,
  Plus,
  Settings,
  ChevronRight,
  ChevronLeft,
  Mail,
  Check
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

export const QuoteForm = () => {
  const { t, language } = useLanguage();

  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    clientType: '',
    need: '',
    location: '',
    surface: '',
    consumption: '',
    roofType: '',
    name: '',
    email: '',
    phone: ''
  });

  const totalSteps = 4;

  const handleSelect = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const nextStep = () => {
    if (step < totalSteps) setStep(step + 1);
  };

  const prevStep = () => {
    if (step > 1) setStep(step - 1);
  };

  const handleSubmit = () => {
    const clientTypeLabel =
      formData.clientType === 'residential'
        ? t('quote.steps.type.residential')
        : t('quote.steps.type.commercial');

    const needLabels = {
      newInstall: t('quote.steps.need.newInstall'),
      upgrade: t('quote.steps.need.upgrade'),
      maintenance: t('quote.steps.need.maintenance')
    };

    const roofLabels = {
      flat: t('quote.steps.details.roofTypes.flat'),
      pitched: t('quote.steps.details.roofTypes.pitched'),
      mixed: t('quote.steps.details.roofTypes.mixed')
    };

    const subject = encodeURIComponent(
      language === 'fr'
        ? `Demande de devis - ${formData.name}`
        : `Quote Request - ${formData.name}`
    );

    const body = encodeURIComponent(
      `${language === 'fr' ? 'DEMANDE DE DEVIS' : 'QUOTE REQUEST'}

${language === 'fr' ? 'Type de client' : 'Client Type'}: ${clientTypeLabel}
${language === 'fr' ? 'Besoin' : 'Need'}: ${needLabels[formData.need] || formData.need}

${language === 'fr' ? 'DÉTAILS DU PROJET' : 'PROJECT DETAILS'}
${language === 'fr' ? 'Code postal' : 'Postal Code'}: ${formData.location}
${language === 'fr' ? 'Surface disponible' : 'Available Surface'}: ${formData.surface} m²
${language === 'fr' ? 'Consommation annuelle' : 'Annual Consumption'}: ${formData.consumption} kWh
${language === 'fr' ? 'Type de toiture' : 'Roof Type'}: ${roofLabels[formData.roofType] || formData.roofType}

${language === 'fr' ? 'COORDONNÉES' : 'CONTACT INFO'}
${language === 'fr' ? 'Nom' : 'Name'}: ${formData.name}
Email: ${formData.email}
${language === 'fr' ? 'Téléphone' : 'Phone'}: ${formData.phone}`
    );

    window.location.href = `mailto:contact@abdoubusiness.fr?subject=${subject}&body=${body}`;
  };

  const canProceed = () => {
    switch (step) {
      case 1:
        return !!formData.clientType;
      case 2:
        return !!formData.need;
      case 3:
        return !!formData.location && !!formData.roofType;
      case 4:
        return !!formData.name && !!formData.email;
      default:
        return false;
    }
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <div className="space-y-4">
            <h3 className="font-outfit text-lg font-semibold text-stone-900 mb-1">
              {t('quote.steps.type.title')}
            </h3>
            <p className="text-sm text-stone-500 mb-4">
              {t('quote.steps.type.subtitle')}
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => handleSelect('clientType', 'residential')}
                className={`p-5 rounded-xl border-2 transition-all text-left ${formData.clientType === 'residential'
                    ? 'border-green-500 bg-green-50'
                    : 'border-stone-200 hover:border-green-300 bg-white'
                  }`}
                data-testid="quote-type-residential"
              >
                <div
                  className={`w-11 h-11 rounded-xl mb-3 flex items-center justify-center ${formData.clientType === 'residential' ? 'bg-green-100' : 'bg-stone-100'
                    }`}
                >
                  <Home
                    className={`w-5 h-5 ${formData.clientType === 'residential'
                        ? 'text-green-600'
                        : 'text-stone-500'
                      }`}
                  />
                </div>
                <p className="font-semibold text-stone-900">
                  {t('quote.steps.type.residential')}
                </p>
                <p className="text-sm text-stone-500">
                  {t('quote.steps.type.residentialDesc')}
                </p>
              </button>

              <button
                type="button"
                onClick={() => handleSelect('clientType', 'commercial')}
                className={`p-5 rounded-xl border-2 transition-all text-left ${formData.clientType === 'commercial'
                    ? 'border-green-500 bg-green-50'
                    : 'border-stone-200 hover:border-green-300 bg-white'
                  }`}
                data-testid="quote-type-commercial"
              >
                <div
                  className={`w-11 h-11 rounded-xl mb-3 flex items-center justify-center ${formData.clientType === 'commercial' ? 'bg-green-100' : 'bg-stone-100'
                    }`}
                >
                  <Building2
                    className={`w-5 h-5 ${formData.clientType === 'commercial'
                        ? 'text-green-600'
                        : 'text-stone-500'
                      }`}
                  />
                </div>
                <p className="font-semibold text-stone-900">
                  {t('quote.steps.type.commercial')}
                </p>
                <p className="text-sm text-stone-500">
                  {t('quote.steps.type.commercialDesc')}
                </p>
              </button>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-4">
            <h3 className="font-outfit text-lg font-semibold text-stone-900 mb-1">
              {t('quote.steps.need.title')}
            </h3>
            <p className="text-sm text-stone-500 mb-4">
              {t('quote.steps.need.subtitle')}
            </p>

            <div className="space-y-3">
              {['newInstall', 'upgrade', 'maintenance'].map((need) => (
                <button
                  key={need}
                  type="button"
                  onClick={() => handleSelect('need', need)}
                  className={`w-full p-4 rounded-xl border-2 transition-all text-left flex items-center gap-4 ${formData.need === need
                      ? 'border-green-500 bg-green-50'
                      : 'border-stone-200 hover:border-green-300 bg-white'
                    }`}
                  data-testid={`quote-need-${need}`}
                >
                  <div
                    className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${formData.need === need ? 'bg-green-100' : 'bg-stone-100'
                      }`}
                  >
                    {need === 'newInstall' && (
                      <Plus
                        className={`w-5 h-5 ${formData.need === need ? 'text-green-600' : 'text-stone-500'
                          }`}
                      />
                    )}
                    {need === 'upgrade' && (
                      <ChevronRight
                        className={`w-5 h-5 ${formData.need === need ? 'text-green-600' : 'text-stone-500'
                          }`}
                      />
                    )}
                    {need === 'maintenance' && (
                      <Settings
                        className={`w-5 h-5 ${formData.need === need ? 'text-green-600' : 'text-stone-500'
                          }`}
                      />
                    )}
                  </div>

                  <div>
                    <p className="font-semibold text-stone-900">
                      {t(`quote.steps.need.${need}`)}
                    </p>
                    <p className="text-sm text-stone-500">
                      {t(`quote.steps.need.${need}Desc`)}
                    </p>
                  </div>

                  {formData.need === need && (
                    <Check className="w-5 h-5 text-green-600 ml-auto" />
                  )}
                </button>
              ))}
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-4">
            <h3 className="font-outfit text-lg font-semibold text-stone-900 mb-1">
              {t('quote.steps.details.title')}
            </h3>
            <p className="text-sm text-stone-500 mb-4">
              {t('quote.steps.details.subtitle')}
            </p>

            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">
                  {t('quote.steps.details.location')}
                </label>
                <Input
                  type="text"
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                  placeholder={t('quote.steps.details.locationPlaceholder')}
                  className="bg-white h-10 text-sm"
                  data-testid="quote-location-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">
                  {t('quote.steps.details.surface')}
                </label>
                <Input
                  type="number"
                  name="surface"
                  value={formData.surface}
                  onChange={handleChange}
                  placeholder={t('quote.steps.details.surfacePlaceholder')}
                  className="bg-white h-10 text-sm"
                  data-testid="quote-surface-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">
                  {t('quote.steps.details.consumption')}
                </label>
                <Input
                  type="number"
                  name="consumption"
                  value={formData.consumption}
                  onChange={handleChange}
                  placeholder={t('quote.steps.details.consumptionPlaceholder')}
                  className="bg-white h-10 text-sm"
                  data-testid="quote-consumption-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">
                  {t('quote.steps.details.roofType')}
                </label>

                <select
                  name="roofType"
                  value={formData.roofType}
                  onChange={(e) => handleSelect('roofType', e.target.value)}
                  className="w-full h-10 rounded-xl border border-stone-300 bg-white px-3 text-sm text-stone-900 outline-none focus:border-green-600 focus:ring-2 focus:ring-green-100"
                  data-testid="quote-roof-select"
                >
                  <option value="" disabled hidden>
                    {t('quote.steps.details.roofType')}
                  </option>
                  <option value="flat">{t('quote.steps.details.roofTypes.flat')}</option>
                  <option value="pitched">{t('quote.steps.details.roofTypes.pitched')}</option>
                  <option value="mixed">{t('quote.steps.details.roofTypes.mixed')}</option>
                </select>
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-4">
            <h3 className="font-outfit text-lg font-semibold text-stone-900 mb-1">
              {t('quote.steps.contact.title')}
            </h3>
            <p className="text-sm text-stone-500 mb-4">
              {t('quote.steps.contact.subtitle')}
            </p>

            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">
                  {t('quote.steps.contact.name')}
                </label>
                <Input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder={t('quote.steps.contact.namePlaceholder')}
                  className="bg-white h-10 text-sm"
                  data-testid="quote-name-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">
                  {t('quote.steps.contact.email')}
                </label>
                <Input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder={t('quote.steps.contact.emailPlaceholder')}
                  className="bg-white h-10 text-sm"
                  data-testid="quote-email-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">
                  {t('quote.steps.contact.phone')}
                </label>
                <Input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder={t('quote.steps.contact.phonePlaceholder')}
                  className="bg-white h-10 text-sm"
                  data-testid="quote-phone-input"
                />
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <section
      id="quote"
      className="py-12 md:py-16 bg-gradient-to-br from-green-50 to-stone-50"
      data-testid="quote-section"
    >
      <div className="container mx-auto px-4 md:px-6 max-w-2xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-10"
        >
          <h2 className="font-outfit text-3xl md:text-4xl font-semibold tracking-tight text-stone-900 mb-3">
            {t('quote.title')}
          </h2>
          <p className="text-base text-stone-600">
            {t('quote.subtitle')}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="bg-white rounded-2xl shadow-lg border border-stone-100 overflow-hidden"
        >
          <div className="px-6 pt-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-stone-500">
                {t('quote.step')} {step} {t('quote.of')} {totalSteps}
              </span>
              <span className="text-sm font-medium text-green-600">
                {Math.round((step / totalSteps) * 100)}%
              </span>
            </div>

            <div className="h-2 bg-stone-100 rounded-full overflow-hidden">
              <motion.div
                className="h-full bg-gradient-to-r from-green-500 to-green-600 rounded-full"
                initial={{ width: 0 }}
                animate={{ width: `${(step / totalSteps) * 100}%` }}
                transition={{ duration: 0.3 }}
              />
            </div>
          </div>

          <div className="p-6">
            <AnimatePresence mode="wait">
              <motion.div
                key={step}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.2 }}
              >
                {renderStep()}
              </motion.div>
            </AnimatePresence>
          </div>

          <div className="px-6 pb-6 flex items-center justify-between">
            <Button
              onClick={prevStep}
              variant="outline"
              disabled={step === 1}
              className={step === 1 ? 'invisible' : ''}
              data-testid="quote-prev-button"
            >
              <ChevronLeft className="w-4 h-4 mr-2" />
              {t('quote.previous')}
            </Button>

            {step < totalSteps ? (
              <Button
                onClick={nextStep}
                disabled={!canProceed()}
                className="bg-green-700 hover:bg-green-800 text-white"
                data-testid="quote-next-button"
              >
                {t('quote.next')}
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button
                onClick={handleSubmit}
                disabled={!canProceed()}
                className="bg-green-700 hover:bg-green-800 text-white"
                data-testid="quote-submit-button"
              >
                <Mail className="w-4 h-4 mr-2" />
                {t('quote.submit')}
              </Button>
            )}
          </div>
        </motion.div>
      </div>
    </section>
  );
};